import { z } from 'zod';

import type { ExcelError, ExcelResult } from '@apple/utils/files/models/excel';
import { createExcelErrorSchema, createExcelResultSchema } from '@apple/utils/files/models/excel';
import { dateTimeSchema } from '@apple/utils/globalization/models';

import type { Product } from '@apple/features/product/models/management';
import {
	excelProductKeySchema,
	manzanitaProductSchema,
} from '@apple/features/product/models/management';
import { type ProductKey, productKeySchema } from '@apple/features/product/models/product';
import type { Propagation } from '@apple/features/store-events/models/change-propagation';
import { propagationSchema } from '@apple/features/store-events/models/change-propagation';

import type { MarketAssociation, StoreAssociation } from '../models/associations';
import { marketAssociationSchema, storeAssociationSchema } from '../models/associations';
import type { StoreEventProductConfiguration } from '../models/store-events';
import { storeEventProductConfigurationSchema } from '../models/store-events';

/**
 * @see Manzanita.Data.Manzanita.ManzanitaProduct
 * @see AppleRetail.Catalog.AppleRetailProduct
 */
export interface AppleRetailProduct extends Product {
	customerGroupAssociations: MarketAssociation[];
	customerAssociations: StoreAssociation[];
	administratorOnly: boolean;
	quantityLimits: QuantityLimit[];
	storeEventConfiguration: StoreEventProductConfiguration | null;
	priceOverrides: PriceOverride[];
	isDeleted: boolean;
	productVintages: ProductVintage[];
}

export interface ProductVintage {
	vintageId: number;
	itemId: ProductKey;
	updatedDate: Date;
}

export interface ProductFormData extends AppleRetailProduct {
	eventRulePropagations?: Propagation[];
	goBack?: boolean;
	vintageIds?: number[];
}

/** AppleRetail.Data.AppleRetail.QuantityLimit */
export interface QuantityLimit {
	itemId: ProductKey;
	accessTypeId: number;
	accessConfig: string | null;
	startDate: Date | null;
	endDate: Date | null;
	maxQuantity: number;
	updatedDate?: Date | null;
}

export interface PriceOverride {
	warehouseLocationId: number;
	sku: string;
	priceOverride: number | null;
	updatedDate?: Date | null;
}

/**
 * @see AppleRetail.Web.Features.Catalog.FeaturedProductManagementController.FeaturedProduct
 */
export interface FeaturedProduct {
	productId: ProductKey;
	title: string;
	description: string | null;
	sortOrder: number;
	createdOn: Date | string;
}

export interface FeaturedProductFilters {
	productId?: ProductKey;
	title?: string;
	createdOn?: Date | string;
}

export const quantityLimitSchema = z.object({
	itemId: productKeySchema,
	accessTypeId: z.number(),
	accessConfig: z.string().nullable(),
	startDate: dateTimeSchema.nullable(),
	endDate: dateTimeSchema.nullable(),
	maxQuantity: z.number(),
	updatedDate: dateTimeSchema.nullable().optional(),
}) as z.ZodType<QuantityLimit>;

export const priceOverrideSchema = z.object({
	warehouseLocationId: z.number(),
	sku: z.string(),
	priceOverride: z.number().nullable(),
	updatedDate: dateTimeSchema.nullable().optional(),
}) as z.ZodType<PriceOverride>;

export const productVintageSchema = z.object({
	vintageId: z.number(),
	itemId: productKeySchema,
	updatedDate: dateTimeSchema,
}) satisfies z.ZodType<ProductVintage>;

export const appleRetailProductSchema = manzanitaProductSchema.and(
	z.object({
		customerGroupAssociations: z.array(marketAssociationSchema),
		customerAssociations: z.array(storeAssociationSchema),
		quantityLimits: z.array(quantityLimitSchema),
		storeEventConfiguration: storeEventProductConfigurationSchema.nullable(),
		administratorOnly: z.boolean(),
		priceOverrides: z.array(priceOverrideSchema),
		isDeleted: z.boolean(),
		productVintages: z.array(productVintageSchema),
	}),
) satisfies z.ZodType<AppleRetailProduct>;

export const productFormData = appleRetailProductSchema.and(
	z.object({ eventRulePropagations: z.array(propagationSchema).optional() }),
) satisfies z.ZodType<ProductFormData>;

export type ProductExcelResult = ExcelResult<AppleRetailProduct, 'itemId', ProductExcelResultError>;
export type ProductExcelResultError = ExcelError<AppleRetailProduct, 'itemId'>;

export const productExcelErrorSchema = createExcelErrorSchema(
	excelProductKeySchema,
) satisfies z.ZodType<ProductExcelResultError>;

export const productExcelResultSchema = createExcelResultSchema(
	appleRetailProductSchema,
	productExcelErrorSchema,
	excelProductKeySchema.shape.itemId,
) satisfies z.ZodType<ProductExcelResult>;

export const featuredProductSchema = z.object({
	productId: productKeySchema,
	title: z.string(),
	description: z.string().nullable(),
	sortOrder: z.number(),
	createdOn: dateTimeSchema,
}) satisfies z.ZodType<FeaturedProduct>;

export const DEFAULT_PRODUCT = {
	itemId: '',
	sku: '',
	title: '',
	description: null,
	unitOfMeasure: '',
	categoryMappings: [],
	keywords: [],
	imagesJson: '[]',
	beginDate: null,
	endDate: null,
	externalUrl: '',
	quantityLimit: null,
	requiresApproval: false,
	isBackorderable: false,
	comments: '',

	allAssociation: null,
	plantAssociations: [],
	countryAssociations: [],
	customerGroupAssociations: [],
	customerAssociations: [],

	newProductIntroductionRecords: [],
	endOfLifeRecords: [],
	priceOverrides: [],
	quantityLimits: [],
	localizations: [],

	showInventory: false,
	showInventoryStatus: false,
	administratorOnly: false,

	storeEventConfiguration: null,
	isDeleted: false,
	productVintages: [],
} as const satisfies AppleRetailProduct;

export const DEFAULT_PRODUCT_FORM_DATA = {
	...DEFAULT_PRODUCT,
	eventRulePropagations: [],
} as const satisfies ProductFormData;
